import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';

const MODULE_KEY_OPTIONS = [
  '', 'users', 'courses', 'batches', 'enrollments', 'students', 'enquiries',
  'attendance', 'course-records', 'assessments', 'course-content',
  'leave', 'payroll', 'tasks', 'notifications', 'resumes', 'timetable',
  'quiz-generator', 'crm', 'hrm', 'skip',
];

const STEPS = ['Source', 'Configure', 'Preview', 'Migrate'];

export default function SAMigrate() {
  const [step, setStep]           = useState(0);
  const [sourceType, setSourceType] = useState('mongodb'); // 'mongodb' | 'json'
  const [mongoUri, setMongoUri]   = useState('mongodb://localhost:27017/training_institute');
  const [scanning, setScanning]   = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [colMap, setColMap]       = useState({});
  const [tenants, setTenants]     = useState([]);
  const [tenantId, setTenantId]   = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview]     = useState(null);
  const [running, setRunning]     = useState(false);
  const [result, setResult]       = useState(null);
  const [err, setErr]             = useState('');

  // JSON import state
  const [jsonFiles, setJsonFiles]   = useState([]); // [{ name, moduleKey, data: [...] }]
  const [jsonParsing, setJsonParsing] = useState(false);
  const fileInputRef = useRef(null);

  // Load tenants
  useEffect(() => {
    api.get('/sa/tenants', { params: { limit: 200 } })
      .then(r => setTenants(r.data?.records || []))
      .catch(() => {});
  }, []);

  const clearError = () => setErr('');

  // ── MongoDB Scan ───────────────────────────────────────────────────────────
  const handleScan = async () => {
    clearError();
    setScanning(true);
    try {
      const { data } = await api.post('/sa/migrate/scan', { mongoUri });
      const d = data.data || data;
      setScanResult(d);
      const map = {};
      (d.collections || []).forEach(c => { map[c.name] = c.suggested || ''; });
      setColMap(map);
      setStep(1);
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setScanning(false);
    }
  };

  // ── JSON File Import ───────────────────────────────────────────────────────
  const handleJsonFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setJsonParsing(true);
    clearError();
    const parsed = [];
    for (const file of files) {
      try {
        const text = await file.text();
        const data = JSON.parse(text);
        const records = Array.isArray(data) ? data : (data.records || data.data || [data]);
        const baseName = file.name.replace(/\.json$/i, '').toLowerCase();
        // Guess module key from filename
        const guessKey = MODULE_KEY_OPTIONS.find(k => k && baseName.includes(k)) || '';
        parsed.push({ name: file.name, baseName, moduleKey: guessKey, data: records, count: records.length });
      } catch (err) {
        setErr(`Failed to parse ${file.name}: ${err.message}`);
      }
    }
    setJsonFiles(parsed);
    // Build colMap for JSON
    const map = {};
    parsed.forEach(f => { map[f.name] = f.moduleKey; });
    setColMap(map);
    setScanResult({ collections: parsed.map(f => ({ name: f.name, count: f.count, suggested: f.moduleKey })), dbName: 'JSON Files' });
    setJsonParsing(false);
    setStep(1);
  };

  // ── Preview ────────────────────────────────────────────────────────────────
  const handlePreview = async () => {
    if (!tenantId) { setErr('Please select a target tenant'); return; }
    clearError();
    setPreviewing(true);

    if (sourceType === 'json') {
      // Local preview for JSON — count records per mapped key
      const rows = [];
      for (const jf of jsonFiles) {
        const mk = colMap[jf.name];
        if (!mk || mk === 'skip') continue;
        rows.push({ collection: jf.name, moduleKey: mk, count: jf.count, existing: 0, toMigrate: jf.count });
      }
      const t = tenants.find(t => t._id === tenantId);
      setPreview({ tenant: { name: t?.name || '', slug: t?.slug || '' }, preview: rows, totalDocs: rows.reduce((s, r) => s + r.count, 0) });
      setStep(2);
      setPreviewing(false);
      return;
    }

    try {
      const activeMap = Object.fromEntries(
        Object.entries(colMap).filter(([, v]) => v && v !== 'skip')
      );
      const { data } = await api.post('/sa/migrate/preview', { mongoUri, tenantId, collectionMap: activeMap });
      setPreview(data.data || data);
      setStep(2);
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setPreviewing(false);
    }
  };

  // ── Run Migration ──────────────────────────────────────────────────────────
  const handleRun = async () => {
    clearError();
    setRunning(true);

    if (sourceType === 'json') {
      // JSON import — call backend endpoint
      try {
        const importData = {};
        for (const jf of jsonFiles) {
          const mk = colMap[jf.name];
          if (!mk || mk === 'skip') continue;
          if (!importData[mk]) importData[mk] = [];
          importData[mk].push(...jf.data);
        }
        const { data } = await api.post('/sa/migrate/import-json', { tenantId, importData });
        setResult(data.data || data);
        setStep(3);
      } catch (e) {
        setErr(e.response?.data?.message || e.message);
      } finally {
        setRunning(false);
      }
      return;
    }

    try {
      const activeMap = Object.fromEntries(
        Object.entries(colMap).filter(([, v]) => v && v !== 'skip')
      );
      const { data } = await api.post('/sa/migrate/run', { mongoUri, tenantId, collectionMap: activeMap });
      setResult(data.data || data);
      setStep(3);
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setRunning(false);
    }
  };

  // ── Stepper ────────────────────────────────────────────────────────────────
  const Stepper = () => (
    <div className="d-flex align-items-center mb-4 gap-2 flex-wrap">
      {STEPS.map((s, i) => (
        <React.Fragment key={s}>
          <div className="d-flex align-items-center gap-2">
            <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
              style={{ width: 32, height: 32, fontSize: 13,
                background: i < step ? '#198754' : i === step ? '#0d6efd' : '#dee2e6',
                color: i <= step ? '#fff' : '#6c757d' }}>
              {i < step ? <i className="bi bi-check" /> : i + 1}
            </div>
            <span className={`fw-semibold small ${i === step ? 'text-primary' : i < step ? 'text-success' : 'text-muted'}`}>{s}</span>
          </div>
          {i < STEPS.length - 1 && <div style={{ flex: 1, minWidth: 24, height: 2, background: i < step ? '#198754' : '#dee2e6' }} />}
        </React.Fragment>
      ))}
    </div>
  );

  // ── Step 0: Source Selection ────────────────────────────────────────────────
  const StepSource = () => (
    <div>
      <h5 className="fw-bold mb-1">Select Data Source</h5>
      <p className="text-muted small mb-3">Choose how to import your data into the multi-tenant system.</p>

      {/* Source type tabs */}
      <div className="d-flex gap-2 mb-4">
        <button className={`btn ${sourceType === 'mongodb' ? 'btn-primary' : 'btn-outline-primary'} d-flex align-items-center gap-2`}
          onClick={() => setSourceType('mongodb')}>
          <i className="bi bi-database" /> MongoDB Database
        </button>
        <button className={`btn ${sourceType === 'json' ? 'btn-primary' : 'btn-outline-primary'} d-flex align-items-center gap-2`}
          onClick={() => setSourceType('json')}>
          <i className="bi bi-filetype-json" /> JSON Files
        </button>
      </div>

      {sourceType === 'mongodb' ? (
        <div>
          <div className="mb-3">
            <label className="form-label fw-semibold">MongoDB URI</label>
            <input className="form-control font-monospace" value={mongoUri}
              onChange={e => setMongoUri(e.target.value)}
              placeholder="mongodb://localhost:27017/your_old_db" />
            <div className="form-text">Include the database name, e.g. <code>mongodb://localhost:27017/training_institute</code></div>
          </div>
          <button className="btn btn-primary" onClick={handleScan} disabled={scanning || !mongoUri}>
            {scanning ? <><span className="spinner-border spinner-border-sm me-2" />Scanning...</> : <><i className="bi bi-plug me-2" />Connect & Scan</>}
          </button>
        </div>
      ) : (
        <div>
          <div className="mb-3">
            <label className="form-label fw-semibold">Upload JSON Files</label>
            <div className="p-4 border border-2 border-dashed rounded-3 text-center bg-light"
              style={{ cursor: 'pointer' }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#0d6efd'; }}
              onDragLeave={e => { e.currentTarget.style.borderColor = '#dee2e6'; }}
              onDrop={e => {
                e.preventDefault();
                e.currentTarget.style.borderColor = '#dee2e6';
                const dt = e.dataTransfer;
                if (dt.files.length) {
                  const fakeEvent = { target: { files: dt.files } };
                  handleJsonFiles(fakeEvent);
                }
              }}>
              <i className="bi bi-cloud-arrow-up fs-1 text-primary d-block mb-2"></i>
              <div className="fw-semibold">Click to select or drag & drop JSON files</div>
              <div className="text-muted small mt-1">
                Each file should contain an array of records, or an object with <code>records</code> / <code>data</code> key.
              </div>
              <div className="text-muted small mt-1">
                Examples: <code>courses.json</code>, <code>users.json</code>, <code>enrollments.json</code>
              </div>
            </div>
            <input ref={fileInputRef} type="file" className="d-none" accept=".json" multiple
              onChange={handleJsonFiles} />
          </div>

          {jsonParsing && (
            <div className="text-center py-3">
              <span className="spinner-border spinner-border-sm text-primary me-2" />
              Parsing files...
            </div>
          )}

          {jsonFiles.length > 0 && (
            <div className="mb-3">
              <div className="fw-semibold small mb-2">Parsed Files:</div>
              {jsonFiles.map(f => (
                <div key={f.name} className="d-flex align-items-center gap-2 mb-1 p-2 rounded bg-light border">
                  <i className="bi bi-file-earmark-code text-primary"></i>
                  <span className="small fw-semibold">{f.name}</span>
                  <span className="badge bg-primary-subtle text-primary">{f.count} records</span>
                  <button className="btn btn-sm btn-outline-danger py-0 px-1 ms-auto"
                    onClick={() => {
                      setJsonFiles(p => p.filter(x => x.name !== f.name));
                      setColMap(p => { const n = {...p}; delete n[f.name]; return n; });
                    }}>
                    <i className="bi bi-x" style={{fontSize:'0.7rem'}}></i>
                  </button>
                </div>
              ))}
              <button className="btn btn-primary mt-2" onClick={() => setStep(1)}>
                <i className="bi bi-arrow-right me-2" />Configure Mapping
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ── Step 1: Configure ──────────────────────────────────────────────────────
  const StepConfigure = () => {
    const cols = scanResult?.collections || [];
    const mapped  = cols.filter(c => colMap[c.name] && colMap[c.name] !== 'skip');
    const unmapped = cols.filter(c => !colMap[c.name]);
    const skipped = cols.filter(c => colMap[c.name] === 'skip');

    return (
      <div>
        <h5 className="fw-bold mb-1">Configure Mapping</h5>
        <p className="text-muted small mb-3">
          Found <strong>{cols.length} {sourceType === 'json' ? 'files' : 'collections'}</strong>
          {scanResult?.dbName && <> in <code>{scanResult.dbName}</code></>}.
          Map each to a module key, or set to <em>Skip</em>.
        </p>

        {/* Target Tenant */}
        <div className="mb-4 p-3 border rounded bg-light">
          <label className="form-label fw-semibold">Target Tenant <span className="text-danger">*</span></label>
          <select className="form-select" value={tenantId} onChange={e => setTenantId(e.target.value)}>
            <option value="">-- select tenant --</option>
            {tenants.map(t => (
              <option key={t._id} value={t._id}>{t.name} ({t.slug})</option>
            ))}
          </select>
          <div className="form-text">All migrated records will be assigned to this tenant.</div>
        </div>

        {/* Mapping table */}
        <div className="table-responsive mb-3" style={{ maxHeight: 400, overflowY: 'auto' }}>
          <table className="table table-sm table-bordered align-middle mb-0">
            <thead className="table-light sticky-top">
              <tr>
                <th>{sourceType === 'json' ? 'File' : 'Collection'}</th>
                <th style={{ width: 90 }}>Records</th>
                <th>Map to Module Key</th>
              </tr>
            </thead>
            <tbody>
              {cols.map(col => (
                <tr key={col.name} className={colMap[col.name] === 'skip' ? 'table-secondary' : !colMap[col.name] ? 'table-warning' : ''}>
                  <td><code>{col.name}</code></td>
                  <td className="text-end">{col.count.toLocaleString()}</td>
                  <td>
                    <select className="form-select form-select-sm"
                      value={colMap[col.name] || ''}
                      onChange={e => setColMap(p => ({ ...p, [col.name]: e.target.value }))}>
                      <option value="">-- unmapped --</option>
                      {MODULE_KEY_OPTIONS.filter(o => o).map(o => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="d-flex gap-2 flex-wrap mb-4">
          <span className="badge bg-success-subtle text-success border border-success-subtle">{mapped.length} mapped</span>
          {unmapped.length > 0 && <span className="badge bg-warning-subtle text-warning border border-warning-subtle">{unmapped.length} unmapped (will be skipped)</span>}
          {skipped.length > 0 && <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle">{skipped.length} skipped</span>}
        </div>

        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={() => setStep(0)}>
            <i className="bi bi-arrow-left me-1" />Back
          </button>
          <button className="btn btn-primary" onClick={handlePreview} disabled={previewing || !tenantId}>
            {previewing ? <><span className="spinner-border spinner-border-sm me-2" />Previewing...</> : <><i className="bi bi-eye me-2" />Preview Migration</>}
          </button>
        </div>
      </div>
    );
  };

  // ── Step 2: Preview ────────────────────────────────────────────────────────
  const StepPreview = () => {
    const rows = preview?.preview || [];
    const totalNew = rows.reduce((s, r) => s + r.toMigrate, 0);
    const totalExisting = rows.reduce((s, r) => s + r.existing, 0);

    return (
      <div>
        <h5 className="fw-bold mb-1">Preview</h5>
        <p className="text-muted small mb-3">
          Migrating to <strong>{preview?.tenant?.name}</strong>.
          {' '}<strong>{totalNew.toLocaleString()}</strong> new records will be created.
          {totalExisting > 0 && <> <span className="text-muted">({totalExisting.toLocaleString()} already exist)</span></>}
        </p>

        <div className="table-responsive mb-4">
          <table className="table table-sm table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th>{sourceType === 'json' ? 'File' : 'Collection'}</th>
                <th>Module Key</th>
                <th className="text-end">Total</th>
                <th className="text-end">Existing</th>
                <th className="text-end">Will Migrate</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.collection + r.moduleKey} className={r.toMigrate === 0 ? 'table-secondary' : ''}>
                  <td><code>{r.collection}</code></td>
                  <td><span className="badge bg-primary-subtle text-primary">{r.moduleKey}</span></td>
                  <td className="text-end">{r.count.toLocaleString()}</td>
                  <td className="text-end text-muted">{r.existing.toLocaleString()}</td>
                  <td className="text-end fw-semibold">{r.toMigrate.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="table-light fw-bold">
              <tr>
                <td colSpan={4} className="text-end">Total new records:</td>
                <td className="text-end">{totalNew.toLocaleString()}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="alert alert-warning d-flex gap-2 mb-4" role="alert">
          <i className="bi bi-exclamation-triangle-fill flex-shrink-0 mt-1" />
          <div className="small">
            <strong>Before you proceed:</strong> Make sure your new DB is backed up. Duplicate records (same <code>_id</code>) are skipped automatically, so re-running is safe.
          </div>
        </div>

        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={() => setStep(1)}>
            <i className="bi bi-arrow-left me-1" />Back
          </button>
          <button className="btn btn-success" onClick={handleRun} disabled={running || totalNew === 0}>
            {running
              ? <><span className="spinner-border spinner-border-sm me-2" />Migrating... please wait</>
              : <><i className="bi bi-arrow-right-circle me-2" />Run Migration</>}
          </button>
          {totalNew === 0 && <span className="text-muted small align-self-center">Nothing new to migrate.</span>}
        </div>
      </div>
    );
  };

  // ── Step 3: Done ───────────────────────────────────────────────────────────
  const StepDone = () => {
    const rows = result?.results || [];
    return (
      <div>
        <div className="text-center mb-4">
          <div style={{ fontSize: 56 }} className="text-success"><i className="bi bi-check-circle-fill"></i></div>
          <h4 className="fw-bold mt-2">Migration Complete!</h4>
          <p className="text-muted">
            <strong>{result?.totalMigrated?.toLocaleString()}</strong> records migrated to <strong>{result?.tenant?.name}</strong>.
            {result?.totalSkipped > 0 && <> {result.totalSkipped.toLocaleString()} skipped (duplicates).</>}
          </p>
        </div>

        <div className="table-responsive mb-4">
          <table className="table table-sm table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th>Source</th>
                <th>Module Key</th>
                <th className="text-end">Migrated</th>
                <th className="text-end">Skipped</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td><code>{r.collection || r.moduleKey}</code></td>
                  <td><span className="badge bg-primary-subtle text-primary">{r.moduleKey}</span></td>
                  <td className="text-end text-success fw-semibold">{r.migrated.toLocaleString()}</td>
                  <td className="text-end text-muted">{r.skipped.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="alert alert-info small mb-4">
          <strong>Next steps:</strong>
          <ol className="mb-0 mt-1">
            <li>Go to <strong>Tenants</strong> &rarr; select <strong>{result?.tenant?.name}</strong> &rarr; enable the modules you migrated.</li>
            <li>Migrated users retain their existing passwords — they can log in immediately.</li>
            <li>Verify data by logging in as the tenant admin.</li>
          </ol>
        </div>

        <button className="btn btn-outline-primary" onClick={() => {
          setStep(0); setScanResult(null); setPreview(null); setResult(null);
          setColMap({}); setTenantId(''); setJsonFiles([]);
        }}>
          <i className="bi bi-arrow-counterclockwise me-2" />Start Another Migration
        </button>
      </div>
    );
  };

  return (
    <div className="container-fluid py-4" style={{ maxWidth: 860 }}>
      <div className="d-flex align-items-center gap-3 mb-4">
        <div className="rounded-3 d-flex align-items-center justify-content-center"
          style={{ width: 48, height: 48, background: 'linear-gradient(135deg,#6610f2,#0d6efd)' }}>
          <i className="bi bi-database-up text-white fs-5" />
        </div>
        <div>
          <h4 className="mb-0 fw-bold">Data Migration</h4>
          <p className="text-muted small mb-0">Import data from MongoDB or JSON files into a tenant</p>
        </div>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-body p-4">
          <Stepper />

          {err && (
            <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3" role="alert">
              <i className="bi bi-exclamation-circle-fill" />
              <span className="small">{err}</span>
              <button className="btn-close ms-auto" style={{ fontSize: 10 }} onClick={clearError} />
            </div>
          )}

          {step === 0 && <StepSource />}
          {step === 1 && <StepConfigure />}
          {step === 2 && <StepPreview />}
          {step === 3 && <StepDone />}
        </div>
      </div>
    </div>
  );
}
